import AppleLogin from "react-apple-login";
import { useRecoilValue } from "recoil";
import LoginButtonTemplatePromotion from "./LoginButtonTemplatePromotion";
import { userSimpleAtom } from "../../recoil/userSimpleAtom";
import { useRef, useState } from "react";
import { isDev } from "../../config";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import useLoginPromotionMutation from "../../hooks/useLoginPromotionMutation";
import { decodeJWT } from "../../utils/jwtUtils";
import useAppleJs from "../../hooks/useAppleJs";

export default function AppleLoginButtonPromotion({
  render,
}: {
  render?: (props: {
    onClick: (e?: any) => void;
    disabled?: boolean;
  }) => JSX.Element;
}) {
  useAutoLoginPromotion();
  const user = useRecoilValue(userSimpleAtom);
  const [isLoading, setIsLoading] = useState(false);
  const { loginAsync } = useLoginPromotionMutation("apple");
  const appleReady = useAppleJs();

  // When using popup, if user closes the popup without completing auth,
  // the library might not call callback. We watch window focus to detect popup close
  // and clear loading state.
  const focusListenerRef = useRef<((this: Window, ev: FocusEvent) => any) | null>(
    null
  );
  const callbackFiredRef = useRef(false);

  const clearFocusListener = () => {
    if (focusListenerRef.current) {
      window.removeEventListener("focus", focusListenerRef.current);
      focusListenerRef.current = null;
    }
  };

  const apple_cliendId = isDev
    ? "dev.orwellhealth.distancing.login"
    : "com.orwellhealth.distancing.login";

  const apple_promotion_redirectUrl = isDev
    ? "https://dev-www.distancing.im/promotion/oauth/apple"
    : `${window.location.origin}/promotion/oauth/apple`;

  // If Apple JS SDK isn't ready, render a disabled-like button (no-op action)
  if (!appleReady) {
    return (
      <LoginButtonTemplatePromotion
        loginType="apple"
        action={() => {}}
        isLoggedIn={!!user?.accessToken}
        isLoading={false}
      />
    );
  }

  return (
    <AppleLogin
      clientId={apple_cliendId}
      redirectURI={apple_promotion_redirectUrl}
      responseType="code id_token"
      responseMode="form_post"
      scope="email"
      usePopup
      callback={(response: any) => {
        try {
          // mark that callback has fired so focus handler won't treat as cancel
          callbackFiredRef.current = true;
          // cleanup focus listener if any
          clearFocusListener();
          const idToken = response?.authorization?.id_token;
          if (idToken) {
            const decodedToken = decodeJWT(idToken);
            const email = decodedToken?.email || undefined;
            // redirect 없이 바로 로그인 처리
            loginAsync({ idToken, email })
              .catch(() => {})
              .finally(() => {
                setIsLoading(false);
                clearFocusListener();
              });
          } else {
            setIsLoading(false);
          }
        } catch (e) {
          setIsLoading(false);
        }
      }}
      render={
        render
          ? render
          : (props) => (
              <LoginButtonTemplatePromotion
                loginType="apple"
                action={() => {
                  setIsLoading(true);
                  // Prepare a focus listener to detect popup close without callback
                  callbackFiredRef.current = false;
                  const onFocus = () => {
                    // delay a bit to give time for callback to fire on success
                    setTimeout(() => {
                      if (!callbackFiredRef.current) {
                        // likely popup closed/cancelled
                        setIsLoading(false);
                        clearFocusListener();
                      }
                    }, 300);
                  };
                  focusListenerRef.current = onFocus;
                  window.addEventListener("focus", onFocus);
                  props.onClick();
                }}
                isLoggedIn={!!user?.accessToken}
                isLoading={isLoading}
              />
            )
      }
    />
  );
}
